import { Fragment, type ReactNode } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  StackLayout,
  HeadingText,
  UtilityText,
  Divider,
  TextAction,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';
import { Link } from 'react-router-dom';
import { SectionCard } from 'components/section-card/section-card.component';

export type FieldRow = {
  // Stable key for the row. Optional — falls back to `label` when omitted.
  id?: string;
  label: string;
  value?: ReactNode;
};

// A single label/value pair within an InfoSection.
export const InfoRow = ({
  label,
  value,
  compactLabel = false,
}: Omit<FieldRow, 'id'> & { compactLabel?: boolean }) => {
  const { formatMessage } = useIntl();
  // Below tablet the label/value stack vertically and left-align (per Figma);
  // at tablet and up they sit on one row with the value right-aligned.
  const isBelowTablet = useMediaQuery(queryHelpers.down('tablet'));
  const hasValue = value !== undefined && value !== null && value !== '';

  return (
    <StackLayout
      orientation={isBelowTablet ? 'vertical' : 'horizontal'}
      horizontalAlignment={isBelowTablet ? 'start' : 'spaceBetween'}
      verticalAlignment={isBelowTablet ? 'top' : 'center'}
      spacing={isBelowTablet ? '$quarter' : '$one'}
      css={{ width: '100%' }}
    >
      <UtilityText
        css={{
          // Figma uses the small $label everywhere on mobile; on desktop the
          // Personal information section uses the larger $bodyOne label.
          typography: compactLabel || isBelowTablet ? '$label' : '$bodyOne',
          color: '$onSurfaceTextPrimary',
        }}
      >
        {label}
      </UtilityText>
      <Box css={{ textAlign: isBelowTablet ? 'left' : 'right' }}>
        {hasValue ? (
          value
        ) : (
          <UtilityText emphasis="subtle" css={{ typography: '$subtitleTwo' }}>
            {formatMessage({ id: 'STR_NOT_SPECIFIED' })}
          </UtilityText>
        )}
      </Box>
    </StackLayout>
  );
};

// Standard styling for a value rendered inside an InfoRow.
export const InfoValueText = ({ children }: { children: ReactNode }) => (
  <UtilityText
    css={{ typography: '$subtitleTwo', color: '$onSurfaceTextPrimary' }}
  >
    {children}
  </UtilityText>
);

// A bordered card with a heading, an optional Edit action, and either a list of
// label/value rows (divided) or a `body` node rendered in their place (e.g. an
// error message), plus an optional footer.
//
// The Edit affordance has two forms: `editLinkTo` renders it as an internal-nav
// Link (for sections edited on a separate screen, e.g. Communication
// Preferences), while `onEdit` renders it as a button (for in-place editors,
// e.g. Personal Information). The props are a discriminated union, so passing
// both is a compile error rather than a silent runtime pick.
type InfoSectionProps = {
  heading: string;
  rows: FieldRow[];
  // Rendered in place of `rows` when provided — e.g. an error/empty message.
  body?: ReactNode;
  footer?: ReactNode;
  compactLabels?: boolean;
} & (
  | { onEdit?: () => void; editLinkTo?: never }
  | { editLinkTo?: string; onEdit?: never }
);

export const InfoSection = ({
  heading,
  rows,
  body,
  footer,
  onEdit,
  editLinkTo,
  compactLabels = false,
}: InfoSectionProps) => {
  const { formatMessage } = useIntl();

  // Unique accessible name per section ("Edit Personal information") so
  // screen-reader users can tell the Edit affordances apart once more than one
  // section is editable. Visible text stays "Edit" for both forms.
  const editLabel = formatMessage({ id: 'STR_EDIT' });
  const editAriaLabel = formatMessage(
    { id: 'STR_EDIT_SECTION' },
    { section: heading },
  );

  // The Edit affordance is a navigation Link when `editLinkTo` is set, an
  // in-place button when `onEdit` is set, or absent (the props union permits
  // only one of the two).
  let editAction: ReactNode = null;
  if (editLinkTo) {
    editAction = (
      <TextAction
        as={Link}
        to={editLinkTo}
        icon="interfaceEdit"
        aria-label={editAriaLabel}
      >
        {editLabel}
      </TextAction>
    );
  } else if (onEdit) {
    editAction = (
      <TextAction
        as="button"
        type="button"
        icon="interfaceEdit"
        onClick={onEdit}
        aria-label={editAriaLabel}
      >
        {editLabel}
      </TextAction>
    );
  }

  return (
    <SectionCard>
      <StackLayout spacing="$oneAndHalf" horizontalAlignment="stretch">
        <StackLayout
          orientation="horizontal"
          horizontalAlignment="spaceBetween"
          verticalAlignment="center"
          spacing="$one"
          css={{ width: '100%' }}
        >
          <HeadingText
            level="2"
            size="sm"
            css={{ typography: '$headingThree' }}
          >
            {heading}
          </HeadingText>
          {editAction}
        </StackLayout>
        {/* `||` (not `??`) so a falsy body — e.g. `body={hasError && <Error/>}`
            resolving to `false` — falls through to the rows rather than
            rendering nothing and suppressing them. */}
        {body || (
          <StackLayout spacing="$threeQuarters" horizontalAlignment="stretch">
            {rows.map((row, index) => (
              <Fragment key={row.id ?? row.label}>
                <InfoRow {...row} compactLabel={compactLabels} />
                {index < rows.length - 1 && <Divider />}
              </Fragment>
            ))}
          </StackLayout>
        )}
        {footer}
      </StackLayout>
    </SectionCard>
  );
};
